import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { scanDni } from '../../lib/dniScanner';
import { getImageUrl } from '../../lib/helpers';
import { Camera, CheckCircle2, LinkIcon, ScanLine, Upload, UserRound } from 'lucide-react';
import './PreRegisterPage.css';

// ── helpers ──────────────────────────────────────────────────────────────────
function toISO(display) {
  if (!display) return '';
  const [d, m, y] = display.split('/');
  return `${y}-${m}-${d}`;
}
function toDisplay(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}
function fmtDate(v) {
  const nums = v.replace(/\D/g, '');
  let r = '';
  for (let i = 0; i < Math.min(nums.length, 8); i++) {
    if (i === 2 || i === 4) r += '/';
    r += nums[i];
  }
  return r;
}

// ── i18n: formulario público en ES / EN / IT / DE / PT ───────────────────────
const LANGS = [
  { code: 'es', label: 'Español',    flag: '🇪🇸' },
  { code: 'en', label: 'English',    flag: '🇬🇧' },
  { code: 'it', label: 'Italiano',   flag: '🇮🇹' },
  { code: 'de', label: 'Deutsch',    flag: '🇩🇪' },
  { code: 'pt', label: 'Português',  flag: '🇵🇹' },
];

// Above the 90s `proxy_read_timeout` on the `/v1` nginx location, so a slow-but-alive
// request still gets its 504 (or its success) instead of being aborted client-side.
const SUBMIT_TIMEOUT_MS = 120000;

// True when we cannot know whether the server persisted the submission: no response at
// all (dead socket), an axios timeout, or a 504 raised while the backend kept working.
// A 502 is deliberately excluded: it usually means the backend was never reached.
// In every other case the server answered, so its `detail` is the truth.
function isAmbiguousOutcome(err) {
  if (err?.code === 'ECONNABORTED') return true;   // axios timeout
  if (!err?.response) return true;                 // no response reached us
  return err.response.status === 504;              // upstream was still working
}

// Every attempt from this page carries the same key, so a submit the server already
// persisted — but whose response we never saw — comes back as that same record instead
// of a second one. Deliberately NOT persisted across reloads: on a shared club tablet a
// stored key would hand the next applicant the previous one's record.
// `uuid` is not a declared dependency of this app; `crypto.randomUUID` needs a secure
// context, which the pre-registration form always has (it handles ID documents).
function newIdempotencyKey() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `k_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

const STRINGS = {
  es: {
    linkInvalid: 'Enlace no válido',
    linkInvalidDesc: 'Este enlace de preinscripción no existe o ha expirado.',
    sentTitle: '¡Solicitud enviada!',
    sentDesc: (name) => `Tu solicitud de preinscripción ha sido recibida por ${name}. El club la revisará y se pondrá en contacto contigo.`,
    subtitle: 'Formulario de preinscripción de socio',
    poweredBy: 'Powered by',
    photoLabel: 'Foto del socio', capture: 'Capturar', cancel: 'Cancelar', takeSelfie: 'Hacer selfie', uploadPhoto: 'Subir foto', retry: 'Repetir',
    scanDni: 'Escanear DNI (opcional — rellena el formulario automáticamente)', front: 'Anverso', back: 'Reverso', dniReadError: 'No se pudo leer el DNI.',
    firstName: 'Nombre *', lastName: 'Apellidos *', dniNie: 'DNI / NIE', birthDate: 'Fecha nacimiento', birthPlaceholder: 'dd/mm/aaaa', phone: 'Teléfono', email: 'Email', address: 'Dirección',
    requiredError: 'Nombre y apellidos son obligatorios.', submitError: 'Error al enviar la solicitud. Inténtalo de nuevo.', submitting: 'Enviando…', submit: 'Enviar solicitud',
    submitMaybeSent: 'Puede que tu solicitud se haya enviado. No la repitas: avisa al personal del club.',
    camHttps: 'La cámara solo está disponible en conexiones seguras (HTTPS). Usa "Subir foto" para seleccionar una imagen.',
    camNoSupport: 'Tu navegador no permite acceder a la cámara. Usa "Subir foto" para seleccionar una imagen.',
    camDenied: 'No se pudo acceder a la cámara. Comprueba que el navegador tiene permiso.',
    camHttp: 'La cámara no está disponible en HTTP. Usa "Subir foto" para seleccionar una imagen.',
  },
  en: {
    linkInvalid: 'Invalid link',
    linkInvalidDesc: 'This pre-registration link does not exist or has expired.',
    sentTitle: 'Request sent!',
    sentDesc: (name) => `Your pre-registration request has been received by ${name}. The club will review it and get in touch with you.`,
    subtitle: 'Member pre-registration form',
    poweredBy: 'Powered by',
    photoLabel: 'Member photo', capture: 'Capture', cancel: 'Cancel', takeSelfie: 'Take selfie', uploadPhoto: 'Upload photo', retry: 'Retake',
    scanDni: 'Scan ID (optional — fills in the form automatically)', front: 'Front', back: 'Back', dniReadError: 'Could not read the ID.',
    firstName: 'First name *', lastName: 'Last name *', dniNie: 'ID / passport', birthDate: 'Date of birth', birthPlaceholder: 'dd/mm/yyyy', phone: 'Phone', email: 'Email', address: 'Address',
    requiredError: 'First name and last name are required.', submitError: 'Error sending the request. Please try again.', submitting: 'Sending…', submit: 'Send request',
    submitMaybeSent: 'Your request may already have been sent. Do not submit it again — please ask a member of staff.',
    camHttps: 'The camera is only available over secure connections (HTTPS). Use "Upload photo" to select an image.',
    camNoSupport: 'Your browser does not allow camera access. Use "Upload photo" to select an image.',
    camDenied: 'Could not access the camera. Check that the browser has permission.',
    camHttp: 'The camera is not available over HTTP. Use "Upload photo" to select an image.',
  },
  it: {
    linkInvalid: 'Link non valido',
    linkInvalidDesc: 'Questo link di preiscrizione non esiste o è scaduto.',
    sentTitle: 'Richiesta inviata!',
    sentDesc: (name) => `La tua richiesta di preiscrizione è stata ricevuta da ${name}. Il club la esaminerà e ti contatterà.`,
    subtitle: 'Modulo di preiscrizione socio',
    poweredBy: 'Powered by',
    photoLabel: 'Foto del socio', capture: 'Scatta', cancel: 'Annulla', takeSelfie: 'Fai un selfie', uploadPhoto: 'Carica foto', retry: 'Riprova',
    scanDni: 'Scansiona il documento (opzionale — compila il modulo automaticamente)', front: 'Fronte', back: 'Retro', dniReadError: 'Impossibile leggere il documento.',
    firstName: 'Nome *', lastName: 'Cognome *', dniNie: 'Documento / passaporto', birthDate: 'Data di nascita', birthPlaceholder: 'gg/mm/aaaa', phone: 'Telefono', email: 'Email', address: 'Indirizzo',
    requiredError: 'Nome e cognome sono obbligatori.', submitError: "Errore nell'invio della richiesta. Riprova.", submitting: 'Invio…', submit: 'Invia richiesta',
    submitMaybeSent: 'La tua richiesta potrebbe essere già stata inviata. Non ripeterla: avvisa il personale del club.',
    camHttps: 'La fotocamera è disponibile solo su connessioni sicure (HTTPS). Usa "Carica foto" per selezionare un\'immagine.',
    camNoSupport: 'Il tuo browser non consente l\'accesso alla fotocamera. Usa "Carica foto" per selezionare un\'immagine.',
    camDenied: 'Impossibile accedere alla fotocamera. Verifica che il browser abbia il permesso.',
    camHttp: 'La fotocamera non è disponibile su HTTP. Usa "Carica foto" per selezionare un\'immagine.',
  },
  de: {
    linkInvalid: 'Ungültiger Link',
    linkInvalidDesc: 'Dieser Voranmeldungslink existiert nicht oder ist abgelaufen.',
    sentTitle: 'Anfrage gesendet!',
    sentDesc: (name) => `Deine Voranmeldung wurde von ${name} empfangen. Der Club prüft sie und wird sich mit dir in Verbindung setzen.`,
    subtitle: 'Voranmeldeformular für Mitglieder',
    poweredBy: 'Powered by',
    photoLabel: 'Foto des Mitglieds', capture: 'Aufnehmen', cancel: 'Abbrechen', takeSelfie: 'Selfie machen', uploadPhoto: 'Foto hochladen', retry: 'Wiederholen',
    scanDni: 'Ausweis scannen (optional — füllt das Formular automatisch aus)', front: 'Vorderseite', back: 'Rückseite', dniReadError: 'Ausweis konnte nicht gelesen werden.',
    firstName: 'Vorname *', lastName: 'Nachname *', dniNie: 'Ausweis / Reisepass', birthDate: 'Geburtsdatum', birthPlaceholder: 'tt/mm/jjjj', phone: 'Telefon', email: 'E-Mail', address: 'Adresse',
    requiredError: 'Vor- und Nachname sind erforderlich.', submitError: 'Fehler beim Senden der Anfrage. Bitte versuche es erneut.', submitting: 'Senden…', submit: 'Anfrage senden',
    submitMaybeSent: 'Deine Anfrage wurde möglicherweise bereits gesendet. Sende sie nicht erneut — wende dich an das Personal des Clubs.',
    camHttps: 'Die Kamera ist nur über sichere Verbindungen (HTTPS) verfügbar. Nutze "Foto hochladen", um ein Bild auszuwählen.',
    camNoSupport: 'Dein Browser erlaubt keinen Kamerazugriff. Nutze "Foto hochladen", um ein Bild auszuwählen.',
    camDenied: 'Kein Zugriff auf die Kamera. Prüfe, ob der Browser die Berechtigung hat.',
    camHttp: 'Die Kamera ist über HTTP nicht verfügbar. Nutze "Foto hochladen", um ein Bild auszuwählen.',
  },
  pt: {
    linkInvalid: 'Link inválido',
    linkInvalidDesc: 'Este link de pré-inscrição não existe ou expirou.',
    sentTitle: 'Pedido enviado!',
    sentDesc: (name) => `O teu pedido de pré-inscrição foi recebido por ${name}. O clube irá analisá-lo e entrará em contacto contigo.`,
    subtitle: 'Formulário de pré-inscrição de sócio',
    poweredBy: 'Powered by',
    photoLabel: 'Foto do sócio', capture: 'Capturar', cancel: 'Cancelar', takeSelfie: 'Tirar selfie', uploadPhoto: 'Carregar foto', retry: 'Repetir',
    scanDni: 'Digitalizar documento (opcional — preenche o formulário automaticamente)', front: 'Frente', back: 'Verso', dniReadError: 'Não foi possível ler o documento.',
    firstName: 'Nome *', lastName: 'Apelidos *', dniNie: 'Documento / passaporte', birthDate: 'Data de nascimento', birthPlaceholder: 'dd/mm/aaaa', phone: 'Telefone', email: 'Email', address: 'Morada',
    requiredError: 'Nome e apelidos são obrigatórios.', submitError: 'Erro ao enviar o pedido. Tenta novamente.', submitting: 'A enviar…', submit: 'Enviar pedido',
    submitMaybeSent: 'O teu pedido pode já ter sido enviado. Não o repitas: avisa o pessoal do clube.',
    camHttps: 'A câmara só está disponível em ligações seguras (HTTPS). Usa "Carregar foto" para selecionar uma imagem.',
    camNoSupport: 'O teu navegador não permite o acesso à câmara. Usa "Carregar foto" para selecionar uma imagem.',
    camDenied: 'Não foi possível aceder à câmara. Verifica se o navegador tem permissão.',
    camHttp: 'A câmara não está disponível em HTTP. Usa "Carregar foto" para selecionar uma imagem.',
  },
};

function detectLang() {
  const nav = (typeof navigator !== 'undefined' && (navigator.language || navigator.userLanguage)) || 'es';
  const code = nav.slice(0, 2).toLowerCase();
  return STRINGS[code] ? code : 'es';
}

// Selector de idioma (banderas). Compacto, para la cabecera.
function LangSelector({ lang, setLang }) {
  return (
    <div className="prf-langs">
      {LANGS.map((l) => (
        <button
          key={l.code}
          type="button"
          onClick={() => setLang(l.code)}
          title={l.label}
          aria-label={l.label}
          aria-pressed={lang === l.code}
          className={`prf-lang${lang === l.code ? ' is-active' : ''}`}
        >
          {l.code.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

// ── VenueLogo — shows venue image, falls back to coloured initial ─────────────
function VenueLogo({ name, imagePath }) {
  const [imgError, setImgError] = React.useState(false);
  const initial = (name || '?')[0].toUpperCase();

  if (imagePath && !imgError) {
    return (
      <img
        src={getImageUrl(imagePath)}
        alt={name}
        onError={() => setImgError(true)}
        className="prf-logo"
      />
    );
  }
  return <div className="prf-logo prf-logo--fallback" aria-hidden="true">{initial}</div>;
}

// ── component ─────────────────────────────────────────────────────────────────
export default function PreRegisterPage() {
  const { venueToken } = useParams();

  // Idioma del formulario público (ES/EN/IT/DE/PT). Por defecto, el del navegador.
  const [lang, setLang] = useState(detectLang);
  const t = STRINGS[lang] || STRINGS.es;

  const [venueInfo, setVenueInfo]       = useState(null);
  const [notFound, setNotFound]         = useState(false);

  // form fields
  const [firstName,  setFirstName]  = useState('');
  const [lastName,   setLastName]   = useState('');
  const [dni,        setDni]        = useState('');
  const [birthDate,  setBirthDate]  = useState('');   // dd/mm/yyyy display
  const [birthISO,   setBirthISO]   = useState('');
  const [phone,      setPhone]      = useState('');
  const [email,      setEmail]      = useState('');
  const [address,    setAddress]    = useState('');

  // files
  const [photo,     setPhoto]     = useState(null);
  const [photoUrl,  setPhotoUrl]  = useState(null);
  const [dniFront,  setDniFront]  = useState(null);
  const [dniBack,   setDniBack]   = useState(null);
  const [dniFrontUrl, setDniFrontUrl] = useState(null);
  const [dniBackUrl,  setDniBackUrl]  = useState(null);

  // camera / scanning
  const [cameraOpen,  setCameraOpen]  = useState(false);   // selfie camera
  const [dniScanning, setDniScanning] = useState(null);    // null | 'front' | 'back'
  const [dniError,    setDniError]    = useState('');
  const [dniPartial,  setDniPartial]  = useState(null);

  const videoRef  = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const photoInputRef = useRef(null);
  const dniCamFrontInputRef = useRef(null);
  const dniCamBackInputRef = useRef(null);

  // ui
  const [submitting, setSubmitting] = useState(false);
  const [submitted,  setSubmitted]  = useState(false);
  const [error,      setError]      = useState('');
  // The request may have reached the server even though we never saw a response
  // (proxy timeout, dead socket). Resubmitting would create a duplicate, so the
  // form stays locked and the member is told to ask staff instead of retrying.
  const [maybeSent,  setMaybeSent]  = useState(false);
  // One key per mount, reused by every attempt: a retry after an error the server
  // actually persisted must resolve to the existing record, not create a new one.
  const idempotencyKey = useRef(null);

  // ── fetch venue info ────────────────────────────────────────────────────────
  useEffect(() => {
    axios.get(`/v1/pre-register/${venueToken}/info`)
      .then(r => setVenueInfo(r.data))
      .catch(() => setNotFound(true));
  }, [venueToken]);

  useEffect(() => {
    const video = videoRef.current;
    const stream = streamRef.current;
    if (!cameraOpen || !video || !stream) return;

    video.srcObject = stream;
    video.play?.().catch(() => {});
  }, [cameraOpen]);

  useEffect(() => () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
  }, []);

  // ── camera helpers ──────────────────────────────────────────────────────────
  const startCamera = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      alert(window.location.protocol === 'http:' ? t.camHttps : t.camNoSupport);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setCameraOpen(true);
    } catch (err) {
      const isHttps = window.location.protocol === 'https:';
      alert(isHttps ? t.camDenied : t.camHttp);
    }
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    setCameraOpen(false);
  };

  const capturePhoto = () => {
    const v = videoRef.current; const c = canvasRef.current;
    if (!v || !c) return;
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext('2d').drawImage(v, 0, 0);
    c.toBlob(blob => {
      const file = new File([blob], 'selfie.jpg', { type: 'image/jpeg' });
      setPhoto(file);
      setPhotoUrl(URL.createObjectURL(blob));
    }, 'image/jpeg', 0.92);
    stopCamera();
  };

  // ── DNI scan ────────────────────────────────────────────────────────────────
  const applyDniData = (data, existing = {}) => {
    if (!data) return;
    const merged = { ...existing, ...Object.fromEntries(Object.entries(data).filter(([, value]) => value != null)) };
    if (merged.firstName) setFirstName(merged.firstName);
    if (merged.lastName) setLastName(merged.lastName);
    if (merged.dni) setDni(merged.dni);
    if (merged.address) setAddress(merged.address);
    if (merged.birthDate) {
      setBirthDate(toDisplay(merged.birthDate.iso));
      setBirthISO(merged.birthDate.iso);
    }
  };

  const processDni = async (file, side) => {
    setDniScanning(side); setDniError('');
    const result = await scanDni(file, side, '');
    setDniScanning(null);
    if (result.data) {
      if (side === 'front') {
        setDniPartial(result.data);
        applyDniData(result.data, {});
      } else {
        applyDniData(result.data, dniPartial || {});
      }
    }
    if (!result.success) setDniError(result.error || t.dniReadError);
  };

  const openDniScanner = (side) => {
    setDniError('');
    if (side === 'front') dniCamFrontInputRef.current?.click();
    else dniCamBackInputRef.current?.click();
  };

  const handleDniDeviceCapture = async (e, side) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (side === 'front') {
      setDniFront(file);
      setDniFrontUrl(URL.createObjectURL(file));
    } else {
      setDniBack(file);
      setDniBackUrl(URL.createObjectURL(file));
    }
    await processDni(file, side);
  };

  // ── submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    // The button is disabled, but Enter inside a text field still submits the form.
    if (submitting || maybeSent) return;
    if (!firstName.trim() || !lastName.trim()) {
      setError(t.requiredError);
      return;
    }
    setSubmitting(true); setError('');
    if (!idempotencyKey.current) idempotencyKey.current = newIdempotencyKey();
    try {
      const form = new FormData();
      form.append('first_name', firstName.trim());
      form.append('last_name',  lastName.trim());
      if (dni)       form.append('dni',        dni.trim());
      if (birthISO)  form.append('birth_date', birthISO);
      if (phone)     form.append('phone',      phone.trim());
      if (email)     form.append('email',      email.trim());
      if (address)   form.append('address',    address.trim());
      if (photo)     form.append('photo',      photo);
      if (dniFront)  form.append('dni_front',  dniFront);
      if (dniBack)   form.append('dni_back',   dniBack);

      await axios.post(`/v1/pre-register/${venueToken}/submit`, form, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Idempotency-Key': idempotencyKey.current,
        },
        // Without this the promise can hang forever (axios defaults to no timeout),
        // leaving the button stuck on "submitting". Kept above the 90s proxy read
        // timeout so we never abort a request the proxy would still have delivered.
        timeout: SUBMIT_TIMEOUT_MS,
      });
      setSubmitted(true);
    } catch (err) {
      if (isAmbiguousOutcome(err)) {
        // We cannot tell whether the server persisted the request. Lock the form.
        setMaybeSent(true);
        setError(t.submitMaybeSent);
      } else {
        setError(err.response?.data?.detail || t.submitError);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── render states ────────────────────────────────────────────────────────────
  if (notFound) return (
    <div className="prf-state">
      <div className="prf-state-langs"><LangSelector lang={lang} setLang={setLang} /></div>
      <div className="prf-state-card">
        <LinkIcon size={40} className="prf-state-icon--error" aria-hidden="true" />
        <h1 className="prf-state-title">{t.linkInvalid}</h1>
        <p className="prf-state-body">{t.linkInvalidDesc}</p>
      </div>
    </div>
  );

  if (!venueInfo) return (
    <div className="prf-state">
      <span className="prf-spinner" role="status" aria-label="…" />
    </div>
  );

  if (submitted) return (
    <div className="prf-state">
      <div className="prf-state-card">
        <CheckCircle2 size={40} className="prf-state-icon--ok" aria-hidden="true" />
        <h1 className="prf-state-title">{t.sentTitle}</h1>
        <p className="prf-state-body">{t.sentDesc(venueInfo.name)}</p>
      </div>
    </div>
  );

  return (
    <div className="prf-page">
      <div className="prf-wrap">

        {/* ── cabecera ── */}
        <header className="prf-card prf-head">
          <div className="prf-head-row">
            <VenueLogo name={venueInfo.name} imagePath={venueInfo.image_path} />
            <div className="prf-head-text">
              <h1 className="prf-club">{venueInfo.name}</h1>
              <p className="prf-subtitle">{t.subtitle}</p>
            </div>
          </div>
          <div className="prf-head-bottom">
            <LangSelector lang={lang} setLang={setLang} />
            <p className="prf-powered">{t.poweredBy} ClubsAgent</p>
          </div>
        </header>

        <form onSubmit={handleSubmit} className="prf-form">
          <div className="prf-card prf-fields">

            {/* ── foto del socio ── */}
            <div className="prf-photo">
              {photoUrl
                ? <img src={photoUrl} alt={t.photoLabel} className="prf-avatar" />
                : (
                  <div className="prf-avatar prf-avatar--empty" aria-hidden="true">
                    <UserRound size={32} />
                  </div>
                )}
              <div className="prf-photo-col">
                <span className="prf-label">{t.photoLabel}</span>
                {cameraOpen ? (
                  <>
                    <video ref={videoRef} autoPlay playsInline muted className="prf-video" />
                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                    <div className="prf-row">
                      <button type="button" className="prf-btn prf-btn--primary" onClick={capturePhoto}>
                        <Camera size={16} aria-hidden="true" /> {t.capture}
                      </button>
                      <button type="button" className="prf-btn" onClick={stopCamera}>{t.cancel}</button>
                    </div>
                  </>
                ) : (
                  <div className="prf-row">
                    <button type="button" className="prf-btn prf-btn--primary" onClick={startCamera}>
                      <Camera size={16} aria-hidden="true" /> {t.takeSelfie}
                    </button>
                    <label className="prf-btn" style={{ cursor: 'pointer' }}>
                      <Upload size={16} aria-hidden="true" /> {t.uploadPhoto}
                      <input
                        ref={photoInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={e => {
                          const f = e.target.files?.[0];
                          e.target.value = '';
                          if (!f) return;
                          setPhoto(f);
                          setPhotoUrl(URL.createObjectURL(f));
                        }}
                      />
                    </label>
                    {photoUrl && (
                      <button type="button" className="prf-btn-link" style={{ flex: '0 0 auto' }} onClick={startCamera}>
                        {t.retry}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* ── escanear DNI ── */}
            <div className="prf-field">
              <span className="prf-label">{t.scanDni}</span>
              <div className="prf-row">
                <button type="button" className="prf-btn" onClick={() => openDniScanner('front')}>
                  {dniScanning === 'front'
                    ? <span className="prf-spinner" />
                    : <><ScanLine size={16} aria-hidden="true" /> {t.front}</>}
                </button>
                <button type="button" className="prf-btn" onClick={() => openDniScanner('back')}>
                  {dniScanning === 'back'
                    ? <span className="prf-spinner" />
                    : <><ScanLine size={16} aria-hidden="true" /> {t.back}</>}
                </button>
              </div>
              {dniError && <p className="prf-hint">{dniError}</p>}
              {(dniFrontUrl || dniBackUrl) && (
                <div className="prf-dni-shots">
                  {dniFrontUrl && <img src={dniFrontUrl} alt={t.front} />}
                  {dniBackUrl && <img src={dniBackUrl} alt={t.back} />}
                </div>
              )}
            </div>

            {/* ── datos ── */}
            <div className="prf-row">
              <label className="prf-field">
                <span className="prf-field-label">{t.firstName}</span>
                <input className="prf-input" value={firstName} onChange={e => setFirstName(e.target.value)} required />
              </label>
              <label className="prf-field">
                <span className="prf-field-label">{t.lastName}</span>
                <input className="prf-input" value={lastName} onChange={e => setLastName(e.target.value)} required />
              </label>
            </div>

            <div className="prf-row">
              <label className="prf-field">
                <span className="prf-field-label">{t.dniNie}</span>
                <input className="prf-input" value={dni} onChange={e => setDni(e.target.value)} />
              </label>
              <label className="prf-field">
                <span className="prf-field-label">{t.birthDate}</span>
                <input
                  className="prf-input"
                  value={birthDate}
                  onChange={e => {
                    const v = fmtDate(e.target.value);
                    setBirthDate(v);
                    if (v.length === 10) setBirthISO(toISO(v));
                  }}
                  placeholder={t.birthPlaceholder}
                  maxLength={10}
                />
              </label>
            </div>

            <label className="prf-field">
              <span className="prf-field-label">{t.phone}</span>
              <input className="prf-input" type="tel" value={phone} onChange={e => setPhone(e.target.value)} />
            </label>

            <label className="prf-field">
              <span className="prf-field-label">{t.email}</span>
              <input className="prf-input" type="email" value={email} onChange={e => setEmail(e.target.value)} />
            </label>

            <label className="prf-field">
              <span className="prf-field-label">{t.address}</span>
              <input className="prf-input" value={address} onChange={e => setAddress(e.target.value)} />
            </label>
          </div>

          {error && <div className="prf-error" role="alert">{error}</div>}

          <button type="submit" className="prf-btn prf-btn--primary prf-btn--block" disabled={submitting || maybeSent}>
            {submitting ? t.submitting : t.submit}
          </button>
        </form>
      </div>

      {/* ── cámara nativa del DNI (oculta, la disparan Anverso/Reverso) ── */}
      <input
        ref={dniCamFrontInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => handleDniDeviceCapture(e, 'front')}
        style={{ display: 'none' }}
      />
      <input
        ref={dniCamBackInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => handleDniDeviceCapture(e, 'back')}
        style={{ display: 'none' }}
      />
    </div>
  );
}
